import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';
import type { Location } from 'history';
import { __RouterContext as RouterContext, useLocation } from 'react-router';

import { resetCompose } from 'mastodon/actions/compose';
import { useAppHistory } from 'mastodon/components/router';
import type { LocationState } from 'mastodon/components/router';
import { ScrollBehaviorContext } from 'mastodon/containers/scroll_container/scroll_context';
import type { ScrollBehaviorContextType } from 'mastodon/containers/scroll_container/scroll_context';
import {
  ColumnIndexContext,
  ColumnsArea,
} from 'mastodon/features/ui/components/columns_area';
import { ColumnsContextProvider } from 'mastodon/features/ui/util/columns_context';
import { isWritePath, WRITING_FEED_STYLE } from 'mastodon/features/whocare_article/editor/paths';
import { newsLandingPath } from 'mastodon/features/whocare_news/landing';
import { useIdentity } from 'mastodon/identity_context';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

import { FOCUS_COMPOSE_EVENT, FOCUS_SEARCH_EVENT } from './events';
import { IconRail } from './icon_rail';

import { readRailExpanded, writeRailExpanded } from './rail_state';
import { useThreePaneTier, useThreePaneWideEnough } from './hooks';
import { RightPane } from './right_pane';
import type { RightPaneMode } from './right_pane';
import { isOverlayPath, isPublishPath, parseStatusPath } from './routes';
import { EmptyStatusPane, StatusPane } from './status_pane';

const messages = defineMessages({
  feed: { id: 'threepane.feed', defaultMessage: 'Timeline' },
  discardConfirm: {
    id: 'threepane.discard_confirm',
    defaultMessage: 'Discard the post you are writing?',
  },
});


const OPEN_STATUS_CLASS = 'threepane-status--open';

/** What the feed column shows while a post is open and nothing else was visited yet (a direct link to a post). */
const defaultBackground = (signedIn: boolean): Location<LocationState> => {
  // signed out: the news page (a concrete tab; a redirect here would replace the post link)
  const pathname = signedIn ? '/home' : newsLandingPath();

  return {
    pathname,
    search: '',
    hash: '',
    state: undefined,
    key: 'threepane-default',
  };
};

const focusSoon = (selector: string) => {
  // the pane may still be sliding in / un-hiding on the next frame
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      document.querySelector<HTMLElement>(selector)?.focus();
    });
  });
};

/** Nothing after the first (empty) paint may change which of the two scroll roots exists. */
const useBodyClass = (name: string) => {
  useLayoutEffect(() => {
    document.body.classList.add(name);

    return () => {
      document.body.classList.remove(name);
    };
  }, [name]);
};

const ThreePaneArea: React.FC<{
  children: React.ReactElement | React.ReactElement[];
}> = ({ children }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const history = useAppHistory();
  const location = useLocation<LocationState>();
  const routerContext = useContext(RouterContext);
  const scrollContext = useContext(ScrollBehaviorContext);
  const tier = useThreePaneTier();
  const { signedIn } = useIdentity();

  useBodyClass('layout-threepane');

  // ---- what is open ---------------------------------------------------------------------
  const status = parseStatusPath(location.pathname);
  const statusId = status?.id;

  // The feed column keeps showing the last location that was not a post / the publish page.
  const [background, setBackground] =
    useState<Location<LocationState>>(() => defaultBackground(signedIn));

  if (!isOverlayPath(location.pathname) && background !== location) {
    setBackground(location);
  }

  // whocare articles: the editor (/write) takes the feed and post columns. Opening a post from there (e.g.
  // right after publishing) must not leave the editor behind it in the feed column.
  const writing = isWritePath(location.pathname);

  if (isOverlayPath(location.pathname) && isWritePath(background.pathname)) {
    setBackground(defaultBackground(signedIn));
  }

  // ---- right column state -----------------------------------------------------------------
  const [mode, setMode] = useState<RightPaneMode>('following');
  const [rightOpen, setRightOpen] = useState(false); // only used on the "tablet" tier

  // The rail can show labels. On the full tier the choice is remembered and pushes the panes aside;
  // on the tablet tier it is a temporary overlay that closes again after navigating.
  const [railPreferred, setRailPreferred] = useState(readRailExpanded);
  const [railPeek, setRailPeek] = useState(false);
  const railExpanded = tier === 'full' ? railPreferred : railPeek;

  const compose = useAppSelector((state) => state.compose);
  const replyTo = (compose.get('in_reply_to') as string | null) ?? null;
  const dirty =
    ((compose.get('text') as string | undefined) ?? '').trim().length > 0 ||
    ((compose.get('media_attachments') as { size: number } | undefined)?.size ??
      0) > 0 ||
    compose.get('poll') != null ||
    compose.get('quoted_status_id') != null ||
    compose.get('id') != null ||
    replyTo != null;

  // Derived state, adjusted while rendering so the composer is visible in the same commit in
  // which the stock code asks it to focus (reply / mention / edit actions).
  const [seenDirty, setSeenDirty] = useState(false);
  const [seenReply, setSeenReply] = useState<string | null>(null);

  if (dirty !== seenDirty) {
    setSeenDirty(dirty);
    setMode(dirty ? 'compose' : 'following');
    setRightOpen(dirty);
  }

  if (replyTo !== seenReply) {
    setSeenReply(replyTo);

    if (replyTo) {
      setMode('compose');
      setRightOpen(true);
    }
  }

  // Entering the editor closes the right column; the rail buttons (compose / following) open it again.
  const [seenWriting, setSeenWriting] = useState(false);

  if (writing !== seenWriting) {
    setSeenWriting(writing);

    if (writing) {
      setRightOpen(false);
    }
  }

  // Replying shows the post being answered in the left column.
  const replyAcct = useAppSelector((state) => {
    if (!replyTo) {
      return undefined;
    }

    const accountId = state.statuses.getIn([replyTo, 'account']) as
      | string
      | undefined;

    return accountId
      ? (state.accounts.getIn([accountId, 'acct']) as string | undefined)
      : undefined;
  });

  useEffect(() => {
    if (replyTo && replyAcct && statusId !== replyTo) {
      history.push(`/@${replyAcct}/${replyTo}`, { focusTarget: false });
    }
    // only when the reply target changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyTo, replyAcct]);

  // The stock "New Post" route has no place of its own here: show the composer instead.
  const publishing = isPublishPath(location.pathname);
  const [seenPublishing, setSeenPublishing] = useState(false);

  if (publishing !== seenPublishing) {
    setSeenPublishing(publishing);

    if (publishing) {
      setMode('compose');
      setRightOpen(true);
    }
  }

  useEffect(() => {
    if (publishing) {
      focusSoon('.threepane__right .autosuggest-textarea__textarea');
      history.replace(background, background.state);
    }
    // only when /publish is entered
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishing]);

  // On the tablet tier the drawer closes again when a post is picked from its list
  // (or after navigating with an empty composer).
  const [seenLocationKey, setSeenLocationKey] = useState(location.key);

  if (location.key !== seenLocationKey) {
    setSeenLocationKey(location.key);

    if (tier === 'tablet' && !publishing) {
      setRightOpen(rightOpen && mode === 'compose' && dirty);
    }

    setRailPeek(false);
  }

  // Hotkeys (n = new post, / = search) are handled by the app shell; make sure the pane is there.
  useEffect(() => {
    const onCompose = () => {
      if (!signedIn) {
        return;
      }

      setMode('compose');
      setRightOpen(true);
      focusSoon('.threepane__right .autosuggest-textarea__textarea');
    };
    const onSearch = () => {
      setRightOpen(true);
      focusSoon('.threepane__right .search__input');
    };

    window.addEventListener(FOCUS_COMPOSE_EVENT, onCompose);
    window.addEventListener(FOCUS_SEARCH_EVENT, onSearch);

    return () => {
      window.removeEventListener(FOCUS_COMPOSE_EVENT, onCompose);
      window.removeEventListener(FOCUS_SEARCH_EVENT, onSearch);
    };
  }, [signedIn]);

  // Outline the post that is open on the left. (A <style> element would be blocked by the CSP.)
  const feedRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = feedRef.current;

    if (!root) {
      return;
    }

    const apply = () => {
      const wanted = statusId && /^\d+$/.test(statusId) ? statusId : null;

      root
        .querySelectorAll<HTMLElement>(`.${OPEN_STATUS_CLASS}`)
        .forEach((element) => {
          if (element.dataset.id !== wanted) {
            element.classList.remove(OPEN_STATUS_CLASS);
          }
        });

      if (wanted) {
        root
          .querySelectorAll<HTMLElement>(`.status[data-id="${wanted}"]`)
          .forEach((element) => {
            element.classList.add(OPEN_STATUS_CLASS);
          });
      }
    };

    let queued = false;

    const observer = new MutationObserver(() => {
      if (queued) {
        return;
      }

      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        apply();
      });
    });

    apply();
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
    };
  }, [statusId]);

  // ---- handlers -----------------------------------------------------------------------------
  const handleCompose = useCallback(() => {
    setMode('compose');
    setRightOpen(true);
    focusSoon('.threepane__right .autosuggest-textarea__textarea');
  }, []);

  const handleSearch = useCallback(() => {
    setRightOpen(true);
    focusSoon('.threepane__right .search__input');
  }, []);

  const handleFollowing = useCallback(() => {
    setMode('following');
    setRightOpen((open) => !open || mode !== 'following');
  }, [mode]);

  const handleToggleRail = useCallback(() => {
    if (tier === 'full') {
      writeRailExpanded(!railPreferred);
      setRailPreferred(!railPreferred);
    } else {
      setRailPeek((open) => !open);
    }
  }, [tier, railPreferred]);

  const handleDiscard = useCallback(() => {
    if (dirty && !window.confirm(intl.formatMessage(messages.discardConfirm))) {
      return;
    }

    dispatch(resetCompose());
    setMode('following');
    setRightOpen(false);
  }, [dirty, dispatch, intl]);

  const handleCloseRight = useCallback(() => {
    setRightOpen(false);
  }, []);

  const handleCloseStatus = useCallback(() => {
    history.push(background, background.state);
  }, [history, background]);

  // ---- the feed column sees the background location ------------------------------------------
  const feedRouter = useMemo(
    () =>
      background === location
        ? routerContext
        : {
            ...routerContext,
            location: background,
            match: {
              path: '/',
              url: '/',
              params: {},
              isExact: background.pathname === '/',
            },
          },
    [routerContext, background, location],
  );

  // Opening / closing a post must not reset the scroll position of the feed behind it.
  const feedScroll = useMemo<ScrollBehaviorContextType | null>(() => {
    if (!scrollContext) {
      return null;
    }

    return {
      ...scrollContext,
      registerElement: (key, element, shouldUpdateScroll) => {
        scrollContext.registerElement(
          key,
          element,
          (prevLocation, nextLocation) => {
            if (
              isOverlayPath(nextLocation.pathname) ||
              (prevLocation && isOverlayPath(prevLocation.pathname))
            ) {
              return false;
            }

            return shouldUpdateScroll(prevLocation, nextLocation);
          },
        );
      },
    };
  }, [scrollContext]);

  const feedChildren = Children.map(children, (child) =>
    isValidElement<{ multiColumn?: boolean }>(child)
      ? cloneElement(child, { multiColumn: true })
      : child,
  );


  return (
    <div
      className={classNames('threepane', {
        'threepane--status-open': !!status,
        'threepane--right-open': rightOpen,
        'threepane--rail-expanded': railExpanded,
        'threepane--writing': writing,
      })}
      data-tier={tier}
      data-right-mode={mode}
    >
      <IconRail
        onCompose={handleCompose}
        onSearch={handleSearch}
        onFollowing={handleFollowing}
        composeActive={mode === 'compose' && (tier === 'full' || rightOpen)}
        followingActive={mode === 'following' && (tier === 'full' || rightOpen)}
        expanded={railExpanded}
        onToggle={handleToggleRail}
      />

      <div className='threepane__content'>
        <main
          ref={feedRef}
          className='threepane__feed'
          style={writing ? WRITING_FEED_STYLE : undefined}
          aria-label={intl.formatMessage(messages.feed)}
        >
          <ScrollBehaviorContext.Provider value={feedScroll}>
            <RouterContext.Provider value={feedRouter}>
              <ColumnsContextProvider multiColumn>
                <ColumnIndexContext.Provider value={1}>
                  {feedChildren}
                </ColumnIndexContext.Provider>
              </ColumnsContextProvider>
            </RouterContext.Provider>
          </ScrollBehaviorContext.Provider>
        </main>

        {status ? (
          <ColumnsContextProvider multiColumn>
            <ColumnIndexContext.Provider value={0}>
              <StatusPane onClose={handleCloseStatus} />
            </ColumnIndexContext.Provider>
          </ColumnsContextProvider>
        ) : (
          tier === 'full' && !writing && <EmptyStatusPane />
        )}

        <aside className='threepane__right' hidden={writing && !rightOpen}>
          <RightPane
            mode={mode}
            onModeChange={setMode}
            onDiscard={handleDiscard}
            onClose={tier === 'tablet' || writing ? handleCloseRight : undefined}
            dirty={dirty}
          />
        </aside>
      </div>
    </div>
  );
};

/**
 * Chooses between the stock columns area and the three-pane layout. The three-pane layout is
 * used for everybody (signed in or not) in the single-column web interface at tablet width and up.
 */
export const LayoutArea = forwardRef<
  HTMLDivElement,
  {
    singleColumn?: boolean;
    minimalShell?: boolean;
    children: React.ReactElement | React.ReactElement[];
  }
>(({ singleColumn, minimalShell, children }, ref) => {
  const wideEnough = useThreePaneWideEnough();

  if (singleColumn && !minimalShell && wideEnough) {
    return <ThreePaneArea>{children}</ThreePaneArea>;
  }

  return (
    <ColumnsArea
      ref={ref}
      singleColumn={singleColumn}
      minimalShell={minimalShell}
    >
      {children}
    </ColumnsArea>
  );
});

LayoutArea.displayName = 'LayoutArea';
